/**
 * ColumnMappingStep — Step 4 of the New Import wizard (FR-03.5).
 *
 * Auto-detected mapping, COLLAPSED by default behind a status header so the
 * happy path (everything auto-mapped) stays quiet and the recruiter only opens
 * it when there's something to review:
 *   - GREEN check  → every column mapped to a standard field (no extras).
 *   - RED triangle → some columns are unrecognized (`stored`) and kept as extras;
 *     the recruiter may want to confirm nothing important is being side-lined —
 *     or a ChopLink KEY column is `missing` from the header (2026-09-28).
 * When expanded, the missing key columns are listed FIRST ("Missing: …"), then
 * the needs-review (`stored`) columns, then the auto-mapped ones. Missing is
 * informational: a header without `LinkedIn URL` is refused at `/complete`
 * and never reaches this step, so what is listed here is a key column a file
 * that passed the gate happens to lack (typically `Job 1 Company`).
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px scale.
 * No inline styles, no arbitrary `text-[Npx]`.
 */

import { AlertTriangle, Check, ChevronRight, Info } from "lucide-react";
import { useState } from "react";

import type { ColumnMapping } from "../parsePreview";

export interface ColumnMappingStepProps {
  mapping: ColumnMapping;
}

export function ColumnMappingStep({ mapping }: ColumnMappingStepProps) {
  const [open, setOpen] = useState(false);

  const missingCount = mapping.missing.length;
  const storedCount = mapping.stored.length;
  const needsReview = storedCount > 0 || missingCount > 0;
  const parts: string[] = [];
  if (missingCount > 0) {
    parts.push(`${missingCount} key ${missingCount === 1 ? "column" : "columns"} missing`);
  }
  if (storedCount > 0) {
    parts.push(
      `${storedCount} extra ${storedCount === 1 ? "column" : "columns"} stored for reference`
    );
  }
  const summary = needsReview
    ? `${parts.join(" · ")} — review`
    : "All standard columns auto-mapped";

  return (
    <div data-testid="column-mapping-step" className="flex flex-col gap-2">
      <button
        type="button"
        data-testid="mapping-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-label text-text hover:border-accent"
      >
        {needsReview ? (
          <AlertTriangle
            data-testid="mapping-status-review"
            className="w-4 h-4 text-danger shrink-0"
            aria-hidden="true"
          />
        ) : (
          <Check
            data-testid="mapping-status-ok"
            className="w-4 h-4 text-success shrink-0"
            aria-hidden="true"
          />
        )}
        <span data-testid="mapping-summary" className="flex-1 text-left">
          {summary}
        </span>
        <ChevronRight
          className={`w-4 h-4 text-text-secondary shrink-0 transition-transform ${
            open ? "rotate-90" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <ul className="flex flex-col gap-1.5">
          {/* Missing KEY columns first: what the file should carry and does not. */}
          {mapping.missing.map((col) => (
            <li
              key={`missing-${col}`}
              data-testid="missing-column"
              className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-caption text-warning-ink"
            >
              <AlertTriangle className="w-4 h-4 text-warning shrink-0" aria-hidden="true" />
              <span className="flex-1">Missing: {col}</span>
            </li>
          ))}
          {/* Then the needs-review (unrecognized) columns. */}
          {mapping.stored.map((col) => (
            <li
              key={`stored-${col}`}
              data-testid="stored-column"
              className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-caption text-text"
            >
              <Info className="w-4 h-4 text-warning shrink-0" aria-hidden="true" />
              <span className="flex-1">{col}</span>
              <span className="text-text-secondary">stored — review</span>
            </li>
          ))}
          {mapping.mapped.map((col) => (
            <li
              key={`mapped-${col}`}
              data-testid="mapped-column"
              className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-caption text-text"
            >
              <Check className="w-4 h-4 text-accent shrink-0" aria-hidden="true" />
              <span className="flex-1">{col}</span>
              <span className="text-text-secondary">mapped</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
